import { Component } from '@angular/core';
import {
	AbstractControl,
	FormBuilder,
	FormControl,
	Validators,
} from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { DcValueAccessorComponent } from '@datachain/ui-sdk/common';
import { takeUntil, tap } from 'rxjs';

import { smallLangPatternValidator } from '../../../ui/form/small-lang-pattern.validator';
import { timezoneValidator } from '../../../ui/form/timezone.validator';
import { ValidationErrorKeys } from '../../../ui/form/validation-erros-keys';
import {
	ExportFileConfigurationEntity,
	FileNameGenStrategy,
} from './export-file-configuration.entity';

interface IFileNamingOptions {
	label: string;
	value: string;
	disabled?: boolean;
}

enum ExportFileFormControls {
	Strategy = 'strategy',
	FileName = 'fileName',
	ShouldApplyCustomExtension = 'shouldApplyCustomExtension',
	CustomExtension = 'customExtension',
}

@Component({
	selector: 'app-export-file-configuration',
	templateUrl: './export-file-configuration.component.html',
	styleUrls: ['./export-file-configuration.component.scss'],
	providers: [
		DcValueAccessorComponent.generateAccessorToken(
			ExportFileConfigurationComponent
		),
		DcValueAccessorComponent.generateValidatorToken(
			ExportFileConfigurationComponent
		),
	],
})
export class ExportFileConfigurationComponent extends DcValueAccessorComponent<
	ExportFileConfigurationComponent,
	ExportFileConfigurationEntity
> {
	public Appearance: MatFormFieldAppearance = 'outline';
	public ExportFileFormControls = ExportFileFormControls;
	public FileNameGenStrategy = FileNameGenStrategy;
	public fileNamingOptions: Array<IFileNamingOptions> = [
		{
			label: $localize`:i18n=@@export.fileName.strategy.uuid:`,
			value: FileNameGenStrategy.UUID,
		},
		{
			label: $localize`:i18n=@@export.fileName.strategy.userDefinedFileName:`,
			value: FileNameGenStrategy.UserDefinedFileNamePattern,
		},
	];

	protected readonly ValidationErrorKeys = ValidationErrorKeys;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private static toEntity(formValues: any): ExportFileConfigurationEntity {
		return ExportFileConfigurationEntity.build({
			strategy: formValues[ExportFileFormControls.Strategy],
			userDefinedLabel: formValues[ExportFileFormControls.FileName],
			shouldApplyCustomExtension:
				formValues[ExportFileFormControls.ShouldApplyCustomExtension],
			customExtension: formValues[ExportFileFormControls.CustomExtension],
		});
	}

	public constructor(private readonly fb: FormBuilder) {
		super();
		this.cmpId = 'export-file-config';

		this.form = this.fb.group({
			[ExportFileFormControls.Strategy]: [
				FileNameGenStrategy.UUID,
				Validators.required,
			],
			[ExportFileFormControls.ShouldApplyCustomExtension]: [false],
			[ExportFileFormControls.CustomExtension]: [
				{
					value: '',
					disabled: true,
				},
			],
		});

		this.form
			.get(ExportFileFormControls.Strategy)
			?.valueChanges.pipe(
				takeUntil(this.onDestroy$),
				tap((val: FileNameGenStrategy) => this.applyStrategy(val))
			)
			.subscribe();

		this.form
			.get(ExportFileFormControls.ShouldApplyCustomExtension)
			?.valueChanges.pipe(
				takeUntil(this.onDestroy$),
				tap((active) => this.applyCustomExtensionState(active))
			)
			.subscribe();

		this.form.valueChanges
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => {
					this.onChange(this.extract());
					Object.keys(this.form.controls).forEach((k) => {
						const ctrl = this.form.get(k) as AbstractControl;
						ctrl.markAsTouched();
					});
				})
			)
			.subscribe();
	}

	public extract(): ExportFileConfigurationEntity {
		const formValues = this.form.getRawValue();
		return ExportFileConfigurationComponent.toEntity(formValues);
	}

	protected writeIntoForm(obj: ExportFileConfigurationEntity): void {
		const isUserDefinedPattern =
			obj.strategy === FileNameGenStrategy.UserDefinedFileNamePattern;

		this.applyStrategy(obj.strategy);
		this.applyCustomExtensionState(obj.shouldApplyCustomExtension);

		this.form.patchValue(
			{
				[ExportFileFormControls.Strategy]: obj.strategy,
				[ExportFileFormControls.ShouldApplyCustomExtension]:
					obj.shouldApplyCustomExtension,
				[ExportFileFormControls.CustomExtension]: obj.customExtension,
				...(isUserDefinedPattern
					? {
							[ExportFileFormControls.FileName]: obj.userDefinedLabel ?? '',
					  }
					: {}),
			},
			{
				emitEvent: false,
			}
		);
	}

	private applyStrategy(strategy: FileNameGenStrategy): void {
		if (strategy === FileNameGenStrategy.UserDefinedFileNamePattern) {
			if (!this.form.get(ExportFileFormControls.FileName)) {
				this.form.addControl(
					ExportFileFormControls.FileName,
					new FormControl('', [
						Validators.required,
						smallLangPatternValidator([
							{
								pattern: '%date',
								openingCompletionChar: '{',
								closingCompletionChar: '}',
							},
							{
								pattern: '%TZ',
								openingCompletionChar: '(',
								closingCompletionChar: ')',
							},
						]),
						timezoneValidator(['/', ':', '\\']),
					]),
					{
						emitEvent: false,
					}
				);
			}
			return;
		}

		if (this.form.get(ExportFileFormControls.FileName)) {
			this.form.removeControl(ExportFileFormControls.FileName, {
				emitEvent: false,
			});
		}
	}

	/** Enables/disables the custom extension control without emitting. */
	private applyCustomExtensionState(active: boolean): void {
		const ctrl = this.form.get(ExportFileFormControls.CustomExtension);
		if (active) {
			ctrl?.enable({
				emitEvent: false,
			});
			return;
		}
		ctrl?.disable({
			emitEvent: false,
		});
	}
}
